import React, { useState } from 'react';
import { bookData } from '../data/mock';
import { Camera, X } from 'lucide-react';

const PlayIcon = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-bg-page/40">
    <div className="w-16 h-16 rounded-full bg-brand-primary/80 flex items-center justify-center">
      <svg className="w-8 h-8 text-text-inverse ml-1" fill="currentColor" viewBox="0 0 20 20">
        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
      </svg>
    </div>
  </div>
);

const GalleryThumbnail = ({ photo }) => {
  const isVideo = photo.type === 'video';
  const src = isVideo ? (photo.thumbnail || photo.image) : photo.image;
  return (
    <div className="relative overflow-hidden rounded-lg border border-border-medium hover:border-brand-primary transition-all duration-300">
      <img
        src={src}
        alt={photo.caption}
        className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
      />
      {isVideo && <PlayIcon />}
      <div className="absolute inset-0 bg-gradient-to-t from-bg-page/90 via-bg-page/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="body-small text-brand-primary font-semibold">
            {isVideo ? 'Clicca per riprodurre' : 'Clicca per ingrandire'}
          </p>
        </div>
      </div>
    </div>
  );
};

const GalleryModalContent = ({ selectedImage }) => {
  if (selectedImage.type === 'video') {
    return (
      <video
        controls
        autoPlay
        className="w-full h-auto rounded-lg border-2 border-brand-primary shadow-2xl"
      >
        <source src={selectedImage.image} type="video/3gpp" />
        <source src={selectedImage.image} type="video/mp4" />
        Il tuo browser non supporta la riproduzione video.
      </video>
    );
  }
  return (
    <img
      src={selectedImage.image}
      alt={selectedImage.caption}
      className="w-full h-auto rounded-lg border-2 border-brand-primary shadow-2xl"
    />
  );
};

export const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('commando');
  const [selectedPhase, setSelectedPhase] = useState('fase1');

  const openModal = (image) => setSelectedImage(image);
  const closeModal = () => setSelectedImage(null);

  const currentCategory = bookData.galleryCategories[selectedCategory];
  const phases = currentCategory?.phases || {};
  const currentPhase = phases[selectedPhase];

  return (
    <section id="galleria" className="section-container bg-bg-page py-24">
      <div className="container mx-auto px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Camera className="w-8 h-8 text-brand-primary" />
              <h2 className="heading-1 text-brand-primary">Galleria Fotografica</h2>
            </div>
            <div className="w-24 h-1 bg-brand-primary mx-auto mb-8"></div>
            <p className="body-medium text-text-secondary">
              Immagini che raccontano le operazioni, gli eventi e la storia
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex justify-center gap-4 mb-12">
            {Object.keys(bookData.galleryCategories).map((catKey) => {
              const cat = bookData.galleryCategories[catKey];
              return (
                <button
                  key={catKey}
                  onClick={() => {
                    setSelectedCategory(catKey);
                    setSelectedPhase(Object.keys(cat.phases)[0]);
                  }}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    selectedCategory === catKey
                      ? 'bg-brand-primary text-text-inverse'
                      : 'bg-bg-card text-text-secondary border border-border-medium hover:border-brand-primary'
                  }`}
                >
                  {cat.title}
                </button>
              );
            })}
          </div>

          {/* Category Description */}
          <div className="text-center mb-12">
            <p className="body-medium text-text-primary mb-2">
              {currentCategory?.description}
            </p>
          </div>

          {/* Phase Tabs (only show if multiple phases exist) */}
          {Object.keys(phases).length > 1 && (
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {Object.keys(phases).map((phaseKey) => {
                const phase = phases[phaseKey];
                return (
                  <button
                    key={phaseKey}
                    onClick={() => setSelectedPhase(phaseKey)}
                    className={`px-5 py-2 rounded-lg font-semibold text-sm transition-all duration-300 ${
                      selectedPhase === phaseKey
                        ? 'bg-secondary-olive text-brand-primary border-2 border-brand-primary'
                        : 'bg-bg-card text-text-secondary border border-border-medium hover:border-brand-primary'
                    }`}
                  >
                    {phase.title}
                  </button>
                );
              })}
            </div>
          )}

          {/* Phase Description */}
          {currentPhase?.description && (
            <div className="text-center mb-8">
              <p className="body-small text-text-secondary italic">
                {currentPhase.description}
              </p>
            </div>
          )}

          {/* Photo Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentPhase?.photos.map((photo) => (
              <div
                key={photo.id}
                className="gallery-item group cursor-pointer"
                onClick={() => openModal(photo)}
              >
                <GalleryThumbnail photo={photo} />
                <p className="caption text-text-secondary mt-3 px-2">
                  {photo.caption}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal for enlarged image/video */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-bg-page/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-bg-card hover:bg-brand-primary transition-colors z-10"
            onClick={closeModal}
          >
            <X className="w-6 h-6 text-text-primary" />
          </button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <GalleryModalContent selectedImage={selectedImage} />
            <p className="body-medium text-text-primary mt-4 text-center px-4">
              {selectedImage.caption}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};
